// ============================================================================
// PulseSurveyPage — page PUBLIQUE de reponse au sondage (/sondage?token=)
// ============================================================================
// PULSE-SUBMIT item 9a (P-PS-4) : l'employe clique le lien tokenise du courriel
// AD-G et repond SANS login. Montee HORS de la garde auth du shell (main.tsx,
// route publique du routeur — App et ses gardes restent INCHANGES). Page
// volontairement SOBRE (formulaire froid depuis un courriel) : AUCUN chrome du
// shell (pas de sidebar/nav), mobile-first, tokens du parc.
//
// Fidele aux formulaires PoC (pulse-form / member-pulse-form / question-fields,
// 7e40e4a) : team_pulse = humeur 1-5 (libelles complets) + 3 zones texte +
// extras ; member_pulse = workload/energy 1-5 (bornes du modele) + extras +
// commentaire. Les extras sont rendus selon leur type (scale_1_5 / yes_no /
// text) sous des champs `cq_<id>` — le SERVEUR re-resout et snapshotte, la page
// n'envoie que des reponses brutes. Zero @gs/api-client (machinerie auth
// inutile) : fetch nu vers /api/pulse (public).

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { GS_COMPONENTS } from '@gs/ui';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { useTranslation } from '../../i18n';

// ─── Types (miroir des DTO serveur pulseSubmit.ts — camelCase API) ────────────

type SurveyKind = 'team_pulse' | 'member_pulse';

interface SurveyQuestion {
  id: string;
  coreKey: 'workload' | 'energy' | 'mood' | null;
  questionType: 'scale_1_5' | 'yes_no' | 'text';
  prompt: string;
  label: string | null;
  low: string | null;
  high: string | null;
  required: boolean;
  sortOrder: number;
}

interface SurveyBundle {
  kind: SurveyKind;
  period: string;
  questions: SurveyQuestion[];
}

/** Etats de la page — un seul a la fois (machine simple, pas de retry). */
type PageState =
  | { phase: 'loading' }
  | { phase: 'blocked'; code: 'noToken' | 'invalid' | 'used' | 'expired' }
  | { phase: 'form'; survey: SurveyBundle }
  | { phase: 'success'; kind: SurveyKind; period: string };

/** Mappe le code stable serveur -> etat bloque de la page. */
function blockedCodeFor(apiCode: string): 'invalid' | 'used' | 'expired' {
  if (apiCode === 'token_used') return 'used';
  if (apiCode === 'token_expired') return 'expired';
  return 'invalid';
}

// ─── Widgets tactiles (parite PoC ScaleField/YesNoField — tokens du parc) ─────

const cellBase =
  'flex min-h-16 cursor-pointer flex-col items-center justify-center gap-1 rounded-lg border '
  + 'p-2 text-center text-sm transition-colors touch-manipulation';
const cellOff =
  'border-slate-200 bg-white text-slate-600 hover:border-brand-400 '
  + 'dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:border-brand-500';
const cellOn =
  'border-brand-400 bg-brand-50 font-medium text-brand-600 '
  + 'dark:border-brand-500 dark:bg-brand-950 dark:text-brand-400';

interface FieldCtx {
  values: Record<string, string>;
  setValue: (name: string, value: string) => void;
}

const SCALE = [1, 2, 3, 4, 5] as const;

/** Echelle 1..5 — grosses cases tactiles, bornes low/high sous 1 et 5 (PoC). */
function ScaleField({ name, low, high, ctx }: {
  name: string; low: string; high: string; ctx: FieldCtx;
}) {
  return (
    <div className="grid grid-cols-5 gap-2">
      {SCALE.map((n) => {
        const value = String(n);
        const active = ctx.values[name] === value;
        return (
          <button
            key={n}
            type="button"
            aria-pressed={active}
            onClick={() => ctx.setValue(name, value)}
            className={`${cellBase} ${active ? cellOn : cellOff}`}
          >
            <span className="text-lg font-semibold">{n}</span>
            <span className="text-xs leading-tight text-slate-400 dark:text-slate-500">
              {n === 1 ? low : n === 5 ? high : ' '}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Humeur d'equipe 1..5 a libelles complets (parite PoC MOOD_OPTIONS). */
function MoodField({ ctx }: { ctx: FieldCtx }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-5 gap-2">
      {SCALE.map((n) => {
        const value = String(n);
        const active = ctx.values.mood_score === value;
        return (
          <button
            key={n}
            type="button"
            aria-pressed={active}
            onClick={() => ctx.setValue('mood_score', value)}
            className={`${cellBase} ${active ? cellOn : cellOff}`}
          >
            <span className="text-lg font-semibold">{n}</span>
            <span className="text-xs leading-tight text-slate-400 dark:text-slate-500">
              {t(`gest:sondage.mood.${n}`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/** Oui/Non — valeurs STOCKEES 'oui'|'non' (parite donnee PoC), libelles i18n. */
function YesNoField({ name, ctx }: { name: string; ctx: FieldCtx }) {
  const { t } = useTranslation();
  const options = [
    { value: 'oui', label: t('gest:sondage.form.yes') },
    { value: 'non', label: t('gest:sondage.form.no') },
  ];
  return (
    <div className="grid grid-cols-2 gap-2">
      {options.map((opt) => {
        const active = ctx.values[name] === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            aria-pressed={active}
            onClick={() => ctx.setValue(name, opt.value)}
            className={`${cellBase} text-base font-medium ${active ? cellOn : cellOff}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function FieldBlock({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="text-base font-medium text-slate-800 dark:text-slate-100">
        {legend}
      </legend>
      {children}
    </fieldset>
  );
}

function TextField({ name, label, placeholder, ctx, rows = 4 }: {
  name: string; label: string; placeholder: string; ctx: FieldCtx; rows?: number;
}) {
  return (
    <div className="flex flex-col gap-2">
      <label htmlFor={name} className="text-base font-medium text-slate-800 dark:text-slate-100">
        {label}
      </label>
      <textarea
        id={name}
        rows={rows}
        placeholder={placeholder}
        value={ctx.values[name] ?? ''}
        onChange={(e) => ctx.setValue(name, e.target.value)}
        className={GS_COMPONENTS.input}
      />
    </div>
  );
}

/** Question supplementaire (champ cq_<id>) rendue selon son type (PoC). */
function ExtraQuestion({ q, ctx }: { q: SurveyQuestion; ctx: FieldCtx }) {
  const { t } = useTranslation();
  const name = `cq_${q.id}`;
  return (
    <FieldBlock legend={q.prompt}>
      {q.questionType === 'scale_1_5' ? (
        <ScaleField name={name} low={q.low ?? ''} high={q.high ?? ''} ctx={ctx} />
      ) : q.questionType === 'yes_no' ? (
        <YesNoField name={name} ctx={ctx} />
      ) : (
        <textarea
          id={name}
          rows={4}
          aria-label={q.prompt}
          placeholder={q.required ? '' : t('gest:sondage.form.optional')}
          value={ctx.values[name] ?? ''}
          onChange={(e) => ctx.setValue(name, e.target.value)}
          className={GS_COMPONENTS.input}
        />
      )}
    </FieldBlock>
  );
}

// ─── Coquille sobre (P-PS-4 : aucun chrome du shell) ──────────────────────────

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-page px-4 py-8 dark:bg-slate-900">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-6">
        <img src="/logo-gs.png" alt="Groupe Signalisation" className="h-10 w-auto self-start dark:hidden" />
        <img src="/logo-gs-white.png" alt="Groupe Signalisation" className="hidden h-10 w-auto self-start dark:block" />
        {children}
      </div>
    </main>
  );
}

function MessageCard({ tone, title, message }: {
  tone: 'error' | 'success'; title: string; message: string;
}) {
  const Icon = tone === 'success' ? CheckCircle2 : AlertTriangle;
  const iconTone = tone === 'success'
    ? 'bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400'
    : 'bg-brand-50 text-brand-600 dark:bg-brand-950 dark:text-brand-400';
  return (
    <div className={`${GS_COMPONENTS.card} flex flex-col items-center gap-3 p-8 text-center`}>
      <div className={`flex h-12 w-12 items-center justify-center rounded-full ${iconTone}`}>
        <Icon className="h-6 w-6" aria-hidden />
      </div>
      <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{title}</h1>
      <p className="text-sm text-slate-600 dark:text-slate-300">{message}</p>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function PulseSurveyPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';

  const [state, setState] = useState<PageState>({ phase: 'loading' });
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ phase: 'blocked', code: 'noToken' });
      return undefined;
    }
    (async () => {
      try {
        const res = await fetch(`/api/pulse/survey?token=${encodeURIComponent(token)}`);
        const body = (await res.json().catch(() => null)) as
          | (SurveyBundle & { code?: string })
          | { code?: string }
          | null;
        if (cancelled) return;
        if (!res.ok) {
          setState({ phase: 'blocked', code: blockedCodeFor(body?.code ?? '') });
          return;
        }
        setState({ phase: 'form', survey: body as SurveyBundle });
      } catch {
        if (!cancelled) setState({ phase: 'blocked', code: 'invalid' });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const ctx: FieldCtx = {
    values,
    setValue: (name, value) => {
      setSubmitError(null);
      setValues((v) => ({ ...v, [name]: value }));
    },
  };

  if (state.phase === 'loading') {
    return (
      <Shell>
        <div className="flex min-h-48 items-center justify-center" role="status" aria-live="polite">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
          <span className="sr-only">{t('gest:sondage.loading')}</span>
        </div>
      </Shell>
    );
  }

  if (state.phase === 'blocked') {
    return (
      <Shell>
        <MessageCard
          tone="error"
          title={t(`gest:sondage.blocked.${state.code}.title`)}
          message={t(`gest:sondage.blocked.${state.code}.message`)}
        />
      </Shell>
    );
  }

  if (state.phase === 'success') {
    return (
      <Shell>
        <MessageCard
          tone="success"
          title={t(`gest:sondage.success.${state.kind}.title`)}
          message={t(`gest:sondage.success.${state.kind}.message`, { period: state.period })}
        />
      </Shell>
    );
  }

  const { survey } = state;
  const extras = survey.questions.filter((q) => q.coreKey == null);
  const core = survey.questions.filter((q) => q.coreKey != null);

  // Validation client (parite PoC validate) : coeurs obligatoires + extras
  // required non vides. L'autorite reste le serveur (Zod + tx).
  const missingCore = survey.kind === 'team_pulse'
    ? !values.mood_score
    : core.some((q) => !values[`score_${q.coreKey}`]);
  const missingRequiredExtra = extras.some(
    (q) => q.required && !(values[`cq_${q.id}`] ?? '').trim(),
  );
  const canSubmit = !missingCore && !missingRequiredExtra && !submitting;

  const collectExtras = (): Record<string, string> => {
    const out: Record<string, string> = {};
    for (const q of extras) {
      const v = (values[`cq_${q.id}`] ?? '').trim();
      if (v) out[`cq_${q.id}`] = v;
    }
    return out;
  };

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError(null);
    const payload = survey.kind === 'team_pulse'
      ? {
          token,
          moodScore: Number(values.mood_score),
          challenges: values.challenges ?? '',
          positives: values.positives ?? '',
          supportNeeded: values.support_needed ?? '',
          extras: collectExtras(),
        }
      : {
          token,
          workloadScore: Number(values.score_workload),
          energyScore: Number(values.score_energy),
          comment: values.comment ?? '',
          extras: collectExtras(),
        };
    try {
      const res = await fetch('/api/pulse/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.ok) {
        setState({ phase: 'success', kind: survey.kind, period: survey.period });
        return;
      }
      const body = (await res.json().catch(() => null)) as { code?: string } | null;
      const code = body?.code ?? '';
      if (code === 'invalid_token' || code === 'token_used' || code === 'token_expired') {
        setState({ phase: 'blocked', code: blockedCodeFor(code) });
        return;
      }
      const known = ['already_submitted', 'no_team', 'member_notfound', 'rate_limited', 'invalid_payload'];
      setSubmitError(t(`gest:sondage.errors.${known.includes(code) ? code : 'server'}`));
    } catch {
      setSubmitError(t('gest:sondage.errors.server'));
    } finally {
      setSubmitting(false);
    }
  }

  const moodQuestion = core.find((q) => q.coreKey === 'mood');

  return (
    <Shell>
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
          {t(`gest:sondage.title.${survey.kind}`)}
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {t(`gest:sondage.subtitle.${survey.kind}`, { period: survey.period })}
        </p>
      </header>

      <form
        className={`${GS_COMPONENTS.card} flex flex-col gap-6 p-5 sm:p-6`}
        onSubmit={(e) => { e.preventDefault(); if (canSubmit) void handleSubmit(); }}
      >
        {survey.kind === 'team_pulse' ? (
          <>
            <FieldBlock legend={moodQuestion?.prompt ?? t('gest:sondage.form.moodFallback')}>
              <MoodField ctx={ctx} />
            </FieldBlock>
            <TextField
              name="challenges"
              label={t('gest:sondage.form.challenges')}
              placeholder={t('gest:sondage.form.optional')}
              ctx={ctx}
            />
            <TextField
              name="positives"
              label={t('gest:sondage.form.positives')}
              placeholder={t('gest:sondage.form.optional')}
              ctx={ctx}
            />
            <TextField
              name="support_needed"
              label={t('gest:sondage.form.support')}
              placeholder={t('gest:sondage.form.optional')}
              ctx={ctx}
            />
            {extras.map((q) => <ExtraQuestion key={q.id} q={q} ctx={ctx} />)}
          </>
        ) : (
          <>
            {core.map((q) => (
              <FieldBlock key={q.id} legend={q.prompt}>
                <ScaleField
                  name={`score_${q.coreKey}`}
                  low={q.low ?? ''}
                  high={q.high ?? ''}
                  ctx={ctx}
                />
              </FieldBlock>
            ))}
            {extras.map((q) => <ExtraQuestion key={q.id} q={q} ctx={ctx} />)}
            <TextField
              name="comment"
              label={t('gest:sondage.form.commentLabel')}
              placeholder={t('gest:sondage.form.commentPlaceholder')}
              ctx={ctx}
              rows={5}
            />
          </>
        )}

        {submitError ? (
          <p role="alert" className="text-sm font-medium text-red-600 dark:text-red-400">
            {submitError}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={!canSubmit}
          className={`${GS_COMPONENTS.btnActionMobile} touch-manipulation disabled:cursor-not-allowed disabled:opacity-50`}
        >
          {submitting
            ? t('gest:sondage.form.submitting')
            : t(`gest:sondage.submit.${survey.kind}`)}
        </button>
        {missingCore || missingRequiredExtra ? (
          <p className="text-xs text-slate-400 dark:text-slate-500">
            {t('gest:sondage.form.missing')}
          </p>
        ) : null}
      </form>
    </Shell>
  );
}
